import { db } from './firebase';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore';
import type { ProjectMeetingActionItem, ProjectMeetingAudience, ProjectMeetingNote } from '../types';
import { firestoreSnapshotListenError } from './firestoreListenDebug';

const COLLECTION = 'projectMeetingNotes';

function parseMeetingAudience(raw: unknown): ProjectMeetingAudience | undefined {
  if (raw === 'internal' || raw === 'external') return raw;
  return undefined;
}

function normalizeActionItem(raw: unknown): ProjectMeetingActionItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const text = typeof o.text === 'string' ? o.text.trim() : '';
  const id = typeof o.id === 'string' ? o.id.trim() : '';
  if (!text || !id) return null;
  return {
    id,
    text,
    assigneeName: typeof o.assigneeName === 'string' ? o.assigneeName : undefined,
    assigneeUserId: typeof o.assigneeUserId === 'string' ? o.assigneeUserId : undefined,
    dueDate: typeof o.dueDate === 'string' ? o.dueDate : undefined,
    status: o.status === 'done' ? 'done' : 'open',
  };
}

function fromFirestore(snap: { id: string; data: () => Record<string, unknown> }): ProjectMeetingNote {
  const d = snap.data();
  const actionItemsRaw = Array.isArray(d.actionItems) ? d.actionItems : [];
  const actionItems: ProjectMeetingActionItem[] = [];
  for (const item of actionItemsRaw) {
    const norm = normalizeActionItem(item);
    if (norm) actionItems.push(norm);
  }

  const sourceRaw = d.source as string | undefined;
  const source: ProjectMeetingNote['source'] =
    sourceRaw === 'lctf_field'
      ? 'lctf_field'
      : sourceRaw === 'granola'
        ? 'granola'
        : 'fathom';

  return {
    id: snap.id,
    fathomMeetingId: (d.fathomMeetingId as string) || '',
    fieldNoteJobId: typeof d.fieldNoteJobId === 'string' ? d.fieldNoteJobId : undefined,
    projectId: (d.projectId as string) || '',
    dealId: (d.dealId as string) || undefined,
    title: (d.title as string) || '',
    meetingAt: (d.meetingAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
    summary: (d.summary as string) || '',
    actionItems,
    participants: Array.isArray(d.participants)
      ? d.participants.filter((x): x is string => typeof x === 'string')
      : undefined,
    audience: parseMeetingAudience(d.audience),
    audienceRationale: typeof d.audienceRationale === 'string' ? d.audienceRationale : undefined,
    location: typeof d.location === 'string' ? d.location : undefined,
    recordingUrl: (d.recordingUrl as string) || undefined,
    transcriptUrl: (d.transcriptUrl as string) || undefined,
    matchRationale: (d.matchRationale as string) || undefined,
    matchConfidence:
      typeof d.matchConfidence === 'number' && Number.isFinite(d.matchConfidence)
        ? d.matchConfidence
        : undefined,
    source,
    opsTaskId: typeof d.opsTaskId === 'string' && d.opsTaskId.trim() ? d.opsTaskId.trim() : undefined,
    createdAt: (d.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
    updatedAt: (d.updatedAt as { toDate?: () => Date })?.toDate?.() ?? new Date(),
  };
}

/** Meeting notes attached to an Operations Hub task (newest first). Staff CRM only. */
export function subscribeOpsTaskMeetingNotes(
  opsTaskId: string,
  callback: (notes: ProjectMeetingNote[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION),
    where('opsTaskId', '==', opsTaskId),
    orderBy('meetingAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => {
      callback(snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() })));
    },
    err => {
      firestoreSnapshotListenError('projectMeetingNotesService', `${COLLECTION}/opsTask/${opsTaskId}`, err);
      onError?.(err as Error);
    },
  );
}

/** Real-time meeting notes for a project (newest first). PM-only Firestore rules. */
export function subscribeProjectMeetingNotes(
  projectId: string,
  callback: (notes: ProjectMeetingNote[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, COLLECTION),
    where('projectId', '==', projectId),
    orderBy('meetingAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => {
      callback(snap.docs.map(d => fromFirestore({ id: d.id, data: () => d.data() })));
    },
    err => {
      firestoreSnapshotListenError('projectMeetingNotesService', `${COLLECTION}/${projectId}`, err);
      onError?.(err as Error);
    },
  );
}
